import React, { useEffect, useState } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, CheckCircle, XCircle, Clock, Image, Star, MessageSquare, TrendingUp, ArrowUpRight } from 'lucide-react';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';

const STAT_CONFIG = [
  { key: 'total_users',            label: 'Total Users',         icon: Users,          accent: '#3B82F6', bg: '#EFF6FF', iconColor: '#2563EB' },
  { key: 'total_members',          label: 'Approved Members',    icon: CheckCircle,    accent: '#10B981', bg: '#ECFDF5', iconColor: '#059669' },
  { key: 'pending_applications',   label: 'Pending Review',      icon: Clock,          accent: '#F59E0B', bg: '#FFFBEB', iconColor: '#D97706' },
  { key: 'rejected_applications',  label: 'Rejected',            icon: XCircle,        accent: '#EF4444', bg: '#FEF2F2', iconColor: '#DC2626' },
  { key: 'total_listings',         label: 'Total Listings',      icon: Image,          accent: '#8B5CF6', bg: '#F5F3FF', iconColor: '#7C3AED' },
  { key: 'active_listings',        label: 'Active Listings',     icon: TrendingUp,     accent: '#06B6D4', bg: '#ECFEFF', iconColor: '#0891B2' },
  { key: 'featured_listings',      label: 'Featured',            icon: Star,           accent: '#1C1C1C', bg: '#FFFBEB', iconColor: '#1C1C1C' },
  { key: 'total_enquiries',        label: 'Conversations',       icon: MessageSquare,  accent: '#6366F1', bg: '#EEF2FF', iconColor: '#4F46E5' },
];

function StatCard({ cfg, value, loading }) {
  const Icon = cfg.icon;
  return (
    <Card className="border-0 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)', backgroundColor: '#FFFFFF' }}>
      <CardContent className="p-0">
        <div className="flex items-center p-5 gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.bg }}>
            <Icon size={22} style={{ color: cfg.iconColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium" style={{ color: '#6B7280' }}>{cfg.label}</p>
            {loading ? (
              <Skeleton className="h-7 w-14 mt-1" />
            ) : (
              <p className="text-2xl font-bold mt-0.5" style={{ color: '#111827', fontFamily: 'var(--font-serif)' }}>{value ?? 0}</p>
            )}
          </div>
          <ArrowUpRight size={14} style={{ color: '#D1D5DB', flexShrink: 0 }} />
        </div>
        {/* Accent bar */}
        <div className="h-0.5" style={{ backgroundColor: cfg.accent, opacity: 0.6 }} />
      </CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="text-xs px-3 py-2 rounded-xl" style={{ backgroundColor: '#1C1C1C', color: '#F5F0E8', border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
      <p className="font-medium mb-1" style={{ color: '#1C1C1C' }}>{label}</p>
      {payload.map((p, i) => <p key={i}>{p.name}: {p.value}</p>)}
    </div>
  );
};

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/admin/analytics')
      .then(r => setData(r.data))
      .catch(() => setError('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  const ov = data?.overview || {};
  const PIE_COLORS = ['#1C1C1C', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444'];

  const outcomeData = [
    { label: 'Pending',  count: ov.pending_applications ?? 0,  fill: '#F59E0B' },
    { label: 'Approved', count: ov.approved_applications ?? 0, fill: '#10B981' },
    { label: 'Rejected', count: ov.rejected_applications ?? 0, fill: '#EF4444' },
  ];

  return (
    <DashboardShell title="Analytics">
      <div className="space-y-8" data-testid="admin-analytics-panel">

        {error && (
          <div className="p-4 rounded-xl text-sm" style={{ backgroundColor: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA' }}>{error}</div>
        )}

        {/* ── Page intro ── */}
        <div>
          <h2 className="text-2xl font-semibold mb-1" style={{ fontFamily: 'var(--font-serif)', color: '#111827' }}>Platform Overview</h2>
          <p className="text-sm" style={{ color: '#6B7280' }}>Live snapshot of members, listings, and activity.</p>
        </div>

        {/* ── Stat grid ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STAT_CONFIG.map(cfg => (
            <StatCard key={cfg.key} cfg={cfg} value={ov[cfg.key]} loading={loading} />
          ))}
        </div>

        {/* ── Charts row ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Applications over time */}
          <div className="lg:col-span-2 rounded-2xl p-6" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="font-semibold text-sm" style={{ color: '#111827' }}>Applications</h3>
                <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>New applications over last 6 months</p>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#ECFDF5', color: '#059669' }}>Live</span>
            </div>
            {loading ? (
              <Skeleton className="h-44 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={data?.monthly_applications || []} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1C1C1C" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#1C1C1C" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="count" name="Applications" stroke="#1C1C1C" strokeWidth={2} fill="url(#goldGrad)" dot={{ fill: '#1C1C1C', r: 3, strokeWidth: 0 }} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Listings by category */}
          <div className="rounded-2xl p-6" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <div className="mb-6">
              <h3 className="font-semibold text-sm" style={{ color: '#111827' }}>By Category</h3>
              <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>Listings distribution</p>
            </div>
            {loading ? (
              <Skeleton className="h-44 w-full" />
            ) : (data?.listings_by_category?.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={data.listings_by_category}
                    dataKey="count"
                    nameKey="category"
                    cx="50%" cy="50%"
                    innerRadius={45}
                    outerRadius={70}
                    paddingAngle={3}
                  >
                    {data.listings_by_category.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-44 flex flex-col items-center justify-center">
                <p className="text-sm" style={{ color: '#9CA3AF' }}>No listing data yet</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Outcome bar chart ── */}
        <div className="rounded-2xl p-6" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
          <div className="flex items-start justify-between mb-6">
            <div>
              <h3 className="font-semibold text-sm" style={{ color: '#111827' }}>Application outcomes</h3>
              <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>Pending vs approved vs rejected</p>
            </div>
          </div>
          {loading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <ResponsiveContainer width="100%" height={130}>
              <BarChart data={outcomeData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Count" radius={[6, 6, 0, 0]} maxBarSize={64}>
                  {outcomeData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* ── Quick actions ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Review applications', sub: `${ov.pending_applications ?? 0} awaiting`, href: '/admin/applications', color: '#F59E0B', bg: '#FFFBEB' },
            { label: 'Manage members',      sub: `${ov.total_members ?? 0} approved`,    href: '/admin/members',      color: '#3B82F6', bg: '#EFF6FF' },
            { label: 'View all listings',   sub: `${ov.active_listings ?? 0} active`,     href: '/admin/listings',     color: '#10B981', bg: '#ECFDF5' },
          ].map(action => (
            <a
              key={action.href}
              href={action.href}
              className="flex items-center gap-4 p-4 rounded-xl transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', textDecoration: 'none' }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: action.bg }}>
                <ArrowUpRight size={16} style={{ color: action.color }} />
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: '#111827' }}>{action.label}</p>
                <p className="text-xs" style={{ color: '#6B7280' }}>{action.sub}</p>
              </div>
            </a>
          ))}
        </div>

      </div>
    </DashboardShell>
  );
}
